/* eslint-disable camelcase */
/* eslint-disable no-shadow */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { FlexColumn, FlexRow } from '@Components/common/Layouts';
import InputLabel from '@Components/common/InputLabel';
import { FormControl, Input } from '@Components/common/FormUI';
import { Button } from '@Components/RadixComponents/Button';
import Icon from '@Components/common/Icon';
import { zodResolver } from '@hookform/resolvers/zod';
import { SignupFormValidationSchema } from '@Validations/signupForm/signupForm';
import { signUpUser } from '@Services/common';
import { toast } from 'react-toastify';

const initialState = {
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  role: 'General User',
};

const SignUp = () => {
  const navigate = useNavigate();

  const [pwVisibility, setPwVisibility] = useState(false);
  const [confirmPwVisibility, setConfirmPwVisibility] = useState(false);

  const formMethods = useForm({
    mode: 'onChange',
    resolver: zodResolver(SignupFormValidationSchema),
    defaultValues: initialState,
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
  } = formMethods;

  const formValues = watch();

  const { mutateAsync: postUserSignUp, isLoading: isUserSignUpLoading } =
    useMutation({
      mutationFn: (payloadData: Record<string, any>) => signUpUser(payloadData),
      onError: (error: Record<string, any>) => {
        toast.error(error?.response?.data?.message || 'User sign-up failed');
      },
    });

  const handlePostUser = async (formData: Record<string, any>) => {
    const { first_name, last_name, email, username, password } = formData;
    await postUserSignUp({
      first_name,
      last_name,
      email,
      username,
      password,
    });
    navigate('./verify-email');
  };

  // the function validates for fcdo email at production
  const handleFormSubmit = async () => {
    await handlePostUser(formValues);
    toast.success(`User  Created successfully`);
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="naxatw-flex naxatw-flex-col naxatw-gap-4"
    >
      <h6 className="naxatw-text-xl naxatw-font-medium naxatw-text-black">
        Sign Up
      </h6>
      <FlexColumn className="naxatw-gap-3">
        <FlexRow className="naxatw-gap-2">
          <FormControl className="naxatw-flex-1">
            <InputLabel label="First Name" astric />
            <Input
              id="first_name"
              placeholder="Enter Name"
              type="text"
              className="naxatw-mt-2"
              {...register('first_name')}
            />
            {errors.first_name && (
              <p className="naxatw-p-1 naxatw-text-sm naxatw-text-red-500">
                {errors.first_name.message}
              </p>
            )}
          </FormControl>
          <FormControl className="naxatw-flex-1">
            <InputLabel label="Last Name" astric />
            <Input
              id="last_name"
              placeholder="Enter Last Name"
              type="text"
              className="naxatw-mt-2"
              {...register('last_name')}
            />
            {errors.last_name && (
              <p className="naxatw-p-1 naxatw-text-sm naxatw-text-red-500">
                {errors.last_name.message}
              </p>
            )}
          </FormControl>
        </FlexRow>
        <FormControl>
          <InputLabel label="Email" astric />
          <Input
            id="email"
            placeholder="Enter your email"
            type="text"
            className="naxatw-mt-2"
            {...register('email')}
          />
          {errors.email && (
            <p className="naxatw-p-1 naxatw-text-sm naxatw-text-red-500">
              {errors.email.message}
            </p>
          )}
        </FormControl>
        <FormControl>
          <InputLabel label="Username" astric />
          <Input
            id="username"
            placeholder="Enter your Username"
            type="text"
            className="naxatw-mt-2"
            {...register('username')}
          />
          {errors.username && (
            <p className="naxatw-p-1 naxatw-text-sm naxatw-text-red-500">
              {errors.username.message}
            </p>
          )}
        </FormControl>
        <FormControl className="naxatw-relative">
          <InputLabel label="Password" astric />
          <Input
            id="password"
            placeholder="Enter your Password"
            type={pwVisibility ? 'text' : 'password'}
            className="naxatw-mt-2 naxatw-pr-10"
            {...register('password')}
          />
          <Icon
            name={pwVisibility ? 'visibility' : 'visibility_off'}
            onClick={() => setPwVisibility(!pwVisibility)}
            className="naxatw-absolute naxatw-right-4 naxatw-top-[2.75rem] naxatw-select-none naxatw-text-xl"
          />
          {errors.password && (
            <p className="naxatw-p-1 naxatw-text-sm naxatw-text-red-500">
              {errors.password.message}
            </p>
          )}
        </FormControl>

        <FormControl className="naxatw-relative">
          <InputLabel label="Confirm Password" astric />
          <Input
            id="confirm-password"
            placeholder="Enter Password Again"
            type={confirmPwVisibility ? 'text' : 'password'}
            className="naxatw-mt-2 naxatw-pr-10"
            {...register('confirmPassword')}
          />
          <Icon
            name={confirmPwVisibility ? 'visibility' : 'visibility_off'}
            onClick={() => setConfirmPwVisibility(!confirmPwVisibility)}
            className="naxatw-absolute naxatw-right-4 naxatw-top-[2.75rem] naxatw-select-none naxatw-text-xl"
          />
          {errors.confirmPassword && (
            <p className="naxatw-p-1 naxatw-text-sm naxatw-text-red-500">
              {errors.confirmPassword.message}
            </p>
          )}
        </FormControl>
      </FlexColumn>
      <FlexColumn>
        <Button
          type="submit"
          isLoading={isUserSignUpLoading}
          disabled={isUserSignUpLoading}
        >
          Signup
        </Button>
        <FlexRow className="naxatw-items-center naxatw-gap-2">
          <p className="naxatw-text-[0.75rem] naxatw-font-medium naxatw-tracking-[-0.015rem] naxatw-text-matt-100">
            Already have an account ?{' '}
          </p>
          <Button
            type="button"
            variant="link"
            className="naxatw-w-fit !naxatw-p-0 naxatw-font-medium"
            onClick={() => {
              navigate('../login');
            }}
          >
            <p className="naxatw-underline">Login</p>
          </Button>
        </FlexRow>
      </FlexColumn>
    </form>
  );
};

export default SignUp;
